/**
 * 모듈 트리 다중 선택
 * - Shift: 같은 부모·같은 타입(계층)에서 앵커~클릭 사이 전부
 * - Ctrl/Cmd: 클릭한 항목만 토글 (사이 항목은 건드리지 않음)
 */

function asVisibleRows(visible) {
  if (!Array.isArray(visible)) return [];
  return visible.map(entry => {
    if (entry?.item) {
      return {
        item: entry.item,
        parentId: entry.parentId ?? null,
        level: entry.level
      };
    }
    return { item: entry, parentId: entry?.parentId ?? null, level: entry?.level };
  });
}

export function idsInSiblingRange(visibleRows, fromId, toId) {
  const rows = asVisibleRows(visibleRows);
  const fromRow = rows.find(row => row.item?.id === fromId);
  const toRow = rows.find(row => row.item?.id === toId);
  if (!toRow?.item?.id) {
    return toId != null ? [toId] : [];
  }
  if (!fromRow?.item?.id) {
    return [toId];
  }
  const sameHierarchy =
    fromRow.item.type === toRow.item.type && fromRow.parentId === toRow.parentId;
  if (!sameHierarchy) {
    return [toId];
  }
  const siblings = rows.filter(
    row =>
      row.item?.type === fromRow.item.type &&
      row.parentId === fromRow.parentId
  );
  const start = siblings.findIndex(row => row.item.id === fromId);
  const end = siblings.findIndex(row => row.item.id === toId);
  if (start < 0 || end < 0) {
    return [toId];
  }
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  return siblings.slice(lo, hi + 1).map(row => row.item.id);
}

/** @deprecated sibling range — 테스트 호환 */
export function idsInVisibleRange(visibleItems, fromId, toId) {
  return idsInSiblingRange(visibleItems, fromId, toId);
}

export function resolveTreeBulkSelection({
  visibleRows = [],
  visibleItems = [],
  selectedIds = [],
  anchorId = null,
  clickedItem,
  shiftKey = false,
  toggleKey = false
} = {}) {
  if (!clickedItem?.id) {
    return { selectedIds: [...selectedIds], anchorId };
  }
  const clickedId = clickedItem.id;
  const rows = visibleRows.length ? visibleRows : visibleItems;

  if (shiftKey) {
    const fromId = anchorId || selectedIds[selectedIds.length - 1] || clickedId;
    return {
      selectedIds: idsInSiblingRange(rows, fromId, clickedId),
      anchorId: fromId
    };
  }

  if (toggleKey) {
    const selected = new Set(selectedIds);
    if (selected.has(clickedId)) {
      selected.delete(clickedId);
    } else {
      selected.add(clickedId);
    }
    const visibleIds = asVisibleRows(rows)
      .map(row => row.item?.id)
      .filter(Boolean);
    const stillHidden = selectedIds.filter(id => selected.has(id) && !visibleIds.includes(id));
    const nextVisible = visibleIds.filter(id => selected.has(id));
    const nextIds = [...stillHidden, ...nextVisible];
    return {
      selectedIds: nextIds,
      anchorId: selected.has(clickedId) ? clickedId : nextIds[nextIds.length - 1] ?? null
    };
  }

  if (selectedIds.length === 1 && selectedIds[0] === clickedId) {
    return { selectedIds: [], anchorId: null };
  }

  return { selectedIds: [clickedId], anchorId: clickedId };
}

export function summarizeTreeBulkSelection(items = []) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const types = new Set(list.map(item => item.type).filter(Boolean));
  const uniqueType = types.size === 1 ? list[0].type : null;
  const count = list.length;
  const canMove =
    count >= 2 && (uniqueType === 'string' || uniqueType === 'module');
  let disableReason = null;
  if (count >= 2 && !canMove) {
    disableReason = types.size > 1 ? 'mixed' : uniqueType === 'array' ? 'array' : 'unsupported';
  }
  return { count, uniqueType, canMove, disableReason };
}

/**
 * 트리 다중 선택 → 지도 피처 합치기 (같은 피처 중복 제거)
 * @param {object[]} items
 * @param {(item: object) => { group?: object[], module?: object[] }} resolveTargets
 */
export function unionMapSelectionTargetsFromTreeItems(items, resolveTargets) {
  const group = [];
  const module = [];
  const seenGroup = new Set();
  const seenModule = new Set();
  const types = new Set();

  (items || []).forEach(item => {
    if (item?.type) types.add(item.type);
    const targets = resolveTargets?.(item) || {};
    (targets.group || []).forEach(feature => {
      if (feature && !seenGroup.has(feature)) {
        seenGroup.add(feature);
        group.push(feature);
      }
    });
    (targets.module || []).forEach(feature => {
      if (feature && !seenModule.has(feature)) {
        seenModule.add(feature);
        module.push(feature);
      }
    });
  });

  return {
    group,
    module,
    types: [...types],
    useModuleSelection: types.size === 1 && types.has('module')
  };
}
